import { useState, useMemo, useCallback } from 'react';
import { Collaborator } from '@/features/Collaborators/Models/collaborator';
import { StatusCard } from '@/components/shared/StatusCards';
import { useSalaryPermissions } from '@/features/Collaborators/hooks/useSalaryPermissions';

export interface SalaryStatusCard extends StatusCard {
    field: 'over12Months' | 'scheduledIncreases' | 'clt' | 'pj';
}

interface SalaryCardFilter {
    field: 'over12Months' | 'scheduledIncreases' | 'clt' | 'pj';
    state: 'matching' | 'not_matching';
}

export const useSalaryCards = (collaborators: Collaborator[]) => {
    const [cardFilter, setCardFilter] = useState<SalaryCardFilter | null>(null);

    // SECURITY: Check if user has permission to view salary data
    const { canViewSalary } = useSalaryPermissions();

    // Funções auxiliares para calcular métricas salariais (consistente com a coluna da tabela)
    const getMonthsSinceLastSalary = (salaries: any[]) => {
        if (!salaries?.length) return 0;
        
        // Filtrar apenas salários até hoje (excluir futuros) - igual à tabela
        const now = new Date();
        const pastSalaries = salaries.filter(salary => 
            new Date(salary.date) <= now
        );
        
        if (!pastSalaries.length) return 0;
        
        const sortedSalaries = [...pastSalaries].sort((a, b) => 
            new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        
        const lastSalaryDate = new Date(sortedSalaries[0].date);
        
        // Cálculo exato igual ao da tabela
        return Math.floor((now.getTime() - lastSalaryDate.getTime()) / (1000 * 60 * 60 * 24 * 30));
    };

    const hasScheduledIncreases = (salaries: any[]) => {
        if (!salaries?.length) return false;
        
        const currentDate = new Date();
        return salaries.some(salary => new Date(salary.date) > currentDate);
    };

    // Função auxiliar para obter o tipo do último salário (igual à lógica da tabela)
    const getLastSalaryType = (salaries: any[]) => {
        if (!salaries?.length) return null;
        
        // Filtrar apenas salários até hoje (mesma lógica da tabela)
        const now = new Date();
        const pastSalaries = salaries.filter(salary => 
            new Date(salary.date) <= now
        );
        
        if (!pastSalaries.length) return null;
        
        const sortedSalaries = [...pastSalaries].sort((a, b) => 
            new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        
        return sortedSalaries[0].salaryType || null;
    };

    // Filtra colaboradores baseado no card selecionado
    const cardFilteredCollaborators = useMemo(() => {
        if (!cardFilter) {
            return collaborators;
        }
        
        const { field, state } = cardFilter;
        return collaborators.filter(collaborator => {
            let matches = false;
            
            switch (field) {
                case 'over12Months':
                    const months = getMonthsSinceLastSalary(collaborator.salaryHistorySalaries || []);
                    matches = months >= 12;
                    break;
                    
                case 'scheduledIncreases':
                    matches = hasScheduledIncreases(collaborator.salaryHistorySalaries || []);
                    break;
                    
                case 'clt':
                    matches = getLastSalaryType(collaborator.salaryHistorySalaries || []) === 'CLT';
                    break;
                    
                case 'pj':
                    matches = getLastSalaryType(collaborator.salaryHistorySalaries || []) === 'PJ';
                    break;
            }
            
            return state === 'matching' ? matches : !matches;
        });
    }, [collaborators, cardFilter]);

    // Calcula estatísticas para os cards
    const stats = useMemo(() => {
        // SECURITY: Return empty array if user doesn't have permission
        if (!canViewSalary) {
            return [];
        }

        const total = collaborators.length;
        if (total === 0) return [];

        const fields: ('over12Months' | 'scheduledIncreases' | 'clt' | 'pj')[] = [
            'over12Months', 
            'scheduledIncreases', 
            'clt', 
            'pj'
        ];
        
        const names = {
            over12Months: 'Mais de 12 meses sem reajuste',
            scheduledIncreases: 'Com aumentos programados',
            clt: 'CLT',
            pj: 'PJ'
        };

        const cardStats = fields.map(field => {
            let matching = 0;
            
            collaborators.forEach(collaborator => {
                let matches = false;
                
                switch (field) {
                    case 'over12Months':
                        const months = getMonthsSinceLastSalary(collaborator.salaryHistorySalaries || []);
                        matches = months >= 12;
                        break;
                        
                    case 'scheduledIncreases':
                        matches = hasScheduledIncreases(collaborator.salaryHistorySalaries || []);
                        break;
                        
                    case 'clt':
                        matches = getLastSalaryType(collaborator.salaryHistorySalaries || []) === 'CLT';
                        break;
                        
                    case 'pj':
                        matches = getLastSalaryType(collaborator.salaryHistorySalaries || []) === 'PJ';
                        break;
                }
                
                if (matches) matching++;
            });
            
            let filterState: 'updated' | 'not_updated' | 'none' = 'none';
            if (cardFilter?.field === field) {
                filterState = cardFilter.state === 'matching' ? 'updated' : 'not_updated';
            }
            
            return {
                name: names[field],
                field,
                stat: `${Math.round((matching / total) * 100)}%`,
                updated: matching,
                total,
                filterState
            } as SalaryStatusCard;
        });
        
        console.log('Salary cards stats calculated:', { total, cardStats, cardFilter });
        return cardStats;
    }, [collaborators, cardFilter, canViewSalary]);

    // Handler para clique nos cards
    const handleCardClick = useCallback((field: 'over12Months' | 'scheduledIncreases' | 'clt' | 'pj') => {
        console.log('Salary card clicked:', field);
        setCardFilter(prev => {
            const newFilter = prev?.field === field
                ? (prev.state === 'matching' ? { field, state: 'not_matching' as const } : null)
                : { field, state: 'matching' as const };
            console.log('Salary card filter changed:', prev, '->', newFilter);
            return newFilter;
        });
    }, []);

    // Reset do filtro de cards
    const resetCardFilter = useCallback(() => {
        setCardFilter(null);
    }, []);

    return {
        cardFilteredCollaborators,
        stats: stats as SalaryStatusCard[],
        cardFilter,
        handleCardClick,
        resetCardFilter
    };
};